// Service content data following ComplyKit's modular, transparent pricing philosophy
// All content is customer-friendly, concise, and jargon-free

export const serviceContent = {
  // ═══════════════════════════════════════════════════════════════════
  // STARTUP SERVICES
  // ═══════════════════════════════════════════════════════════════════
  
  'proprietorship': {
    name: 'Sole Proprietorship Registration',
    category: 'Startup',
    categorySlug: 'startup',
    overview: 'Sole Proprietorship is ideal for individuals starting a small business in their own name with minimal compliance and low setup cost. Perfect for freelancers, consultants, and small traders.',
    
    whoShouldChoose: [
      'Freelancers & consultants',
      'Small traders or shop owners',
      'First-time business owners',
      'Low-risk, single-owner businesses',
    ],
    
    whatsIncluded: [
      'Consultation with compliance expert',
      'Name & business structure guidance',
      'Udyam (MSME) registration',
      'GST registration (if required)',
      'Shop & Establishment support (state-based)',
      'PAN application assistance',
    ],
    
    howItWorks: [
      'Share basic business details',
      'Upload required documents',
      'We file and track registrations',
      'Receive certificates & next-steps guidance',
    ],
    
    documentsRequired: [
      'PAN & Aadhaar of proprietor',
      'Business address proof',
      'Bank account details',
      'Passport-size photograph',
    ],
    
    taxationStructure: [
      'Business income taxed as individual income',
      'Filed under ITR-3 or ITR-4',
      'GST applicable if turnover crosses threshold',
      'No separate business tax return required',
    ],
    
    timeline: '5–10 working days',
    timelineNote: 'May vary by state and department processing',
    
    pricing: {
      professional: 1499,
      governmentFees: 'At actuals (paid directly to authorities)',
      notes: ['No hidden charges', 'Govt fees extra'],
    },
    
    notIncluded: [
      'Ongoing GST filings',
      'Income tax return filing',
      'Trademark registration',
    ],
  },

  'partnership': {
    name: 'Partnership Firm Registration',
    category: 'Startup',
    categorySlug: 'startup',
    overview: 'Partnership Firm is suitable for two or more individuals starting a business together with shared profits and responsibilities. Simple structure with minimal regulatory requirements.',
    
    whoShouldChoose: [
      'Two or more individuals starting a joint venture',
      'Professional services (CA, lawyers, doctors)',
      'Family businesses',
      'Small to medium trading businesses',
    ],
    
    whatsIncluded: [
      'Partnership deed drafting',
      'Firm registration with Registrar',
      'PAN application for the firm',
      'GST registration (if applicable)',
      'Partner documentation support',
      'Compliance checklist & guidance',
    ],
    
    howItWorks: [
      'Share partner details & business nature',
      'Review and finalize partnership deed',
      'Upload documents for all partners',
      'Receive registration certificate',
    ],
    
    documentsRequired: [
      'PAN & Aadhaar of all partners',
      'Address proof of partners',
      'Business address proof',
      'Passport-size photographs',
      'Partnership deed (we help draft)',
    ],
    
    taxationStructure: [
      'Firm taxed at flat 30% on profits',
      'Partners pay tax on their share separately',
      'Filed under ITR-5 for the firm',
      'GST applicable based on turnover',
    ],
    
    timeline: '7–14 working days',
    timelineNote: 'Varies by state registrar processing',
    
    pricing: {
      professional: 3999,
      governmentFees: 'At actuals (stamp duty varies by state)',
      notes: ['Partnership deed drafting included', 'Govt fees extra'],
    },
    
    notIncluded: [
      'Ongoing compliance filings',
      'Annual tax return filing',
      'Partner exit/addition changes',
    ],
  },

  'opc': {
    name: 'One Person Company (OPC)',
    category: 'Startup',
    categorySlug: 'startup',
    overview: 'One Person Company combines the benefits of a private limited company with single ownership. Ideal for solo entrepreneurs who want limited liability protection.',
    
    whoShouldChoose: [
      'Solo entrepreneurs seeking limited liability',
      'Professionals wanting corporate structure',
      'Small business owners planning to scale',
      'Individuals with turnover under ₹2 crore',
    ],
    
    whatsIncluded: [
      'Name reservation & approval',
      'MOA & AOA drafting',
      'Incorporation certificate',
      'PAN & TAN for the company',
      'DIN for director',
      'First board resolution templates',
    ],
    
    howItWorks: [
      'Reserve company name with MCA',
      'Prepare incorporation documents',
      'File SPICe+ form with MCA',
      'Receive Certificate of Incorporation',
    ],
    
    documentsRequired: [
      'PAN & Aadhaar of director',
      'Passport-size photograph',
      'Address proof of director',
      'Registered office address proof',
      'Nominee details (PAN & consent)',
    ],
    
    taxationStructure: [
      'Taxed at 25% for turnover under ₹400 crore',
      'Filed under ITR-6',
      'Must convert to Pvt Ltd if turnover exceeds ₹2 crore',
      'GST applicable based on business activity',
    ],
    
    timeline: '7–12 working days',
    timelineNote: 'Subject to MCA processing and name availability',
    
    pricing: {
      professional: 7499,
      governmentFees: 'At actuals (varies by authorized capital)',
      notes: ['Includes DSC for director', 'Govt fees extra'],
    },
    
    notIncluded: [
      'Annual MCA compliance',
      'Accounting & bookkeeping',
      'Income tax return filing',
    ],
  },

  'llp': {
    name: 'Limited Liability Partnership (LLP)',
    category: 'Startup',
    categorySlug: 'startup',
    overview: 'LLP offers the flexibility of a partnership with limited liability protection. Popular among professionals and small businesses seeking liability protection without complex compliance.',
    
    whoShouldChoose: [
      'Professional services firms',
      'Startups with 2+ partners',
      'Businesses seeking limited liability',
      'Those wanting simpler compliance than Pvt Ltd',
    ],
    
    whatsIncluded: [
      'Name reservation with MCA',
      'LLP Agreement drafting',
      'Incorporation filing',
      'PAN & TAN for LLP',
      'DIN for designated partners',
      'Digital signatures (DSC)',
    ],
    
    howItWorks: [
      'Reserve LLP name with MCA',
      'Draft and finalize LLP Agreement',
      'File incorporation documents',
      'Receive Certificate of Incorporation',
    ],
    
    documentsRequired: [
      'PAN & Aadhaar of all partners',
      'Address proof of partners',
      'Registered office address proof',
      'Passport-size photographs',
      'No objection from property owner',
    ],
    
    taxationStructure: [
      'LLP taxed at 30% flat rate',
      'No dividend distribution tax',
      'Partners taxed separately on remuneration',
      'Filed under ITR-5',
    ],
    
    timeline: '10–15 working days',
    timelineNote: 'Subject to MCA processing',
    
    pricing: {
      professional: 6999,
      governmentFees: 'At actuals (varies by contribution)',
      notes: ['LLP Agreement included', 'Govt fees extra'],
    },
    
    notIncluded: [
      'Annual compliance (Form 8 & 11)',
      'Accounting & audit',
      'Income tax return filing',
    ],
  },

  'private-limited': {
    name: 'Private Limited Company',
    category: 'Startup',
    categorySlug: 'startup',
    overview: 'Private Limited Company is the most preferred structure for startups and growing businesses. Offers limited liability, easy fundraising, and credibility with clients and investors.',
    
    whoShouldChoose: [
      'Startups planning to raise funding',
      'Businesses with multiple founders',
      'Companies seeking investor credibility',
      'Businesses planning significant growth',
    ],
    
    whatsIncluded: [
      'Name reservation & approval',
      'MOA & AOA drafting',
      'SPICe+ incorporation filing',
      'PAN, TAN & GST registration',
      'DIN for all directors',
      'Digital signatures for directors',
      'Share certificates',
      'First board resolution templates',
    ],
    
    howItWorks: [
      'Reserve company name with MCA',
      'Prepare MOA, AOA & incorporation docs',
      'File SPICe+ with MCA',
      'Receive Certificate of Incorporation',
    ],
    
    documentsRequired: [
      'PAN & Aadhaar of all directors',
      'Passport (for foreign directors)',
      'Address proof of directors',
      'Registered office address proof',
      'Passport-size photographs',
      'Utility bill (not older than 2 months)',
    ],
    
    taxationStructure: [
      'Taxed at 25% for turnover under ₹400 crore',
      'Dividend taxed in hands of shareholders',
      'Filed under ITR-6',
      'Subject to audit if turnover exceeds threshold',
    ],
    
    timeline: '10–15 working days',
    timelineNote: 'Subject to MCA processing and RoC workload',
    
    pricing: {
      professional: 7999,
      governmentFees: 'At actuals (varies by authorized capital)',
      notes: ['All inclusive package', 'Govt fees extra'],
    },
    
    notIncluded: [
      'Annual ROC compliance',
      'Statutory audit',
      'Income tax return filing',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // GST SERVICES
  // ═══════════════════════════════════════════════════════════════════

  'gst-registration': {
    name: 'GST Registration',
    category: 'GST',
    categorySlug: 'gst',
    overview: 'GST Registration is mandatory for businesses with turnover above threshold or those involved in interstate supply. Get your GSTIN and start billing with tax compliance.',
    
    whoShouldChoose: [
      'Businesses with turnover above ₹40 lakh (₹20 lakh for services)',
      'Interstate sellers or e-commerce operators',
      'Importers and exporters',
      'Those wanting to claim input tax credit',
    ],
    
    whatsIncluded: [
      'GST application preparation',
      'Document verification',
      'Application filing on GST portal',
      'ARN tracking & follow-up',
      'GSTIN certificate delivery',
      'Basic GST compliance guidance',
    ],
    
    howItWorks: [
      'Share business & proprietor/director details',
      'Upload required documents',
      'We file GST REG-01 application',
      'Receive GSTIN within processing time',
    ],
    
    documentsRequired: [
      'PAN of business/proprietor',
      'Aadhaar of authorized signatory',
      'Business address proof',
      'Bank account statement/cancelled cheque',
      'Photograph of proprietor/partners/directors',
      'Authorization letter (for companies)',
    ],
    
    taxationStructure: [
      'GST rates: 5%, 12%, 18%, 28% based on goods/services',
      'Composition scheme available for small businesses',
      'Input tax credit available on business purchases',
      'Monthly/quarterly filing based on turnover',
    ],
    
    timeline: '3–7 working days',
    timelineNote: 'Subject to verification and officer processing',
    
    pricing: {
      professional: 1499,
      governmentFees: 'Nil (no government fee for registration)',
      notes: ['Includes document preparation', 'Govt fees: Nil'],
    },
    
    notIncluded: [
      'Monthly GST return filing',
      'GST reconciliation',
      'GST notice handling',
    ],
  },

  'gst-return': {
    name: 'Monthly GST Filing',
    category: 'GST',
    categorySlug: 'gst',
    overview: 'Regular GST return filing ensures compliance and avoids penalties. We handle GSTR-1, GSTR-3B, and other returns with accuracy and on-time submission.',
    
    whoShouldChoose: [
      'All GST-registered businesses',
      'Businesses seeking timely compliance',
      'Those wanting to maximize input credit',
      'Companies needing reconciliation support',
    ],
    
    whatsIncluded: [
      'GSTR-1 preparation & filing (outward supplies)',
      'GSTR-3B preparation & filing (summary return)',
      'Input tax credit reconciliation',
      'Invoice matching & error correction',
      'Filing confirmation & acknowledgment',
      'Monthly compliance reminders',
    ],
    
    howItWorks: [
      'Share sales & purchase data monthly',
      'We reconcile and prepare returns',
      'Review summary before filing',
      'Returns filed before due date',
    ],
    
    documentsRequired: [
      'Sales invoices for the month',
      'Purchase invoices with GST details',
      'Credit/debit notes if any',
      'Previous return copies',
      'GST portal credentials (or authorization)',
    ],
    
    taxationStructure: [
      'GSTR-1 due by 11th of following month',
      'GSTR-3B due by 20th of following month',
      'Late fees: ₹50/day (₹20 for nil returns)',
      'Interest on late tax payment: 18% p.a.',
    ],
    
    timeline: 'Filed within 2–3 days of receiving data',
    timelineNote: 'Must receive data at least 5 days before due date',
    
    pricing: {
      professional: 999,
      governmentFees: 'Nil (filing has no government fee)',
      notes: ['Per month pricing', 'Govt fees: Nil'],
    },
    
    notIncluded: [
      'GST audit',
      'Annual return (GSTR-9)',
      'GST notice response',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // INCOME TAX SERVICES
  // ═══════════════════════════════════════════════════════════════════

  'itr-efiling': {
    name: 'Individual ITR Filing',
    category: 'Income Tax',
    categorySlug: 'income-tax',
    overview: 'File your income tax return accurately and on time. We handle salary, capital gains, house property, and other income sources with maximum tax savings.',
    
    whoShouldChoose: [
      'Salaried individuals',
      'Freelancers & consultants',
      'Individuals with multiple income sources',
      'Those with capital gains or rental income',
    ],
    
    whatsIncluded: [
      'Form 16 analysis & verification',
      'Income computation from all sources',
      'Deduction optimization (80C, 80D, etc.)',
      'ITR form selection & preparation',
      'E-filing with acknowledgment',
      'Refund tracking support',
    ],
    
    howItWorks: [
      'Share Form 16 & other income documents',
      'We compute income & identify deductions',
      'Review draft return before filing',
      'Receive ITR-V acknowledgment',
    ],
    
    documentsRequired: [
      'Form 16 from employer',
      'Bank statements (interest income)',
      'Investment proofs (80C, 80D, etc.)',
      'Capital gains statements (if any)',
      'Rent receipts & housing loan certificate',
      'PAN & Aadhaar',
    ],
    
    taxationStructure: [
      'New tax regime: 5% to 30% (no deductions)',
      'Old regime: 5% to 30% (with deductions)',
      'Rebate under 87A for income up to ₹7 lakh (new regime)',
      'Due date: July 31 for non-audit cases',
    ],
    
    timeline: 'Same day to 2 working days',
    timelineNote: 'Depends on complexity and document availability',
    
    pricing: {
      professional: 999,
      governmentFees: 'Nil (no government fee for filing)',
      notes: ['For salaried individuals', 'Govt fees: Nil'],
    },
    
    notIncluded: [
      'Tax planning consultation',
      'Notice response handling',
      'Revised return filing',
    ],
  },

  'business-tax': {
    name: 'Business ITR Filing',
    category: 'Income Tax',
    categorySlug: 'income-tax',
    overview: 'Comprehensive tax filing for business owners and professionals. Covers business income, expenses, depreciation, and applicable deductions.',
    
    whoShouldChoose: [
      'Proprietorship business owners',
      'Freelancers & professionals',
      'Small business owners',
      'Traders & commission agents',
    ],
    
    whatsIncluded: [
      'Business income computation',
      'Expense categorization & verification',
      'Depreciation calculation',
      'Presumptive taxation assessment (44AD/44ADA)',
      'ITR-3/ITR-4 preparation & filing',
      'Tax payment challan support',
    ],
    
    howItWorks: [
      'Share business financials & bank statements',
      'We categorize income & expenses',
      'Compute tax liability with deductions',
      'File return & provide acknowledgment',
    ],
    
    documentsRequired: [
      'Bank statements for the year',
      'Sales & purchase invoices',
      'Expense bills & receipts',
      'Previous year ITR',
      'TDS certificates (Form 26AS)',
      'GST returns (if applicable)',
    ],
    
    taxationStructure: [
      'Presumptive: 6% of digital turnover, 8% of cash',
      'Regular: Actual profit taxed at slab rates',
      'Advance tax applicable if liability > ₹10,000',
      'Due date: July 31 (non-audit) / Oct 31 (audit)',
    ],
    
    timeline: '3–5 working days',
    timelineNote: 'Subject to data completeness',
    
    pricing: {
      professional: 2499,
      governmentFees: 'Nil (no government fee for filing)',
      notes: ['Includes expense categorization', 'Govt fees: Nil'],
    },
    
    notIncluded: [
      'Full year bookkeeping',
      'GST return filing',
      'Tax audit (if applicable)',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // MCA COMPLIANCE
  // ═══════════════════════════════════════════════════════════════════

  'company-compliance': {
    name: 'Annual Company Compliance',
    category: 'MCA',
    categorySlug: 'mca',
    overview: 'Annual MCA compliance package for Private Limited Companies. Covers all mandatory filings including AOC-4, MGT-7, and director KYC to keep your company in good standing.',
    
    whoShouldChoose: [
      'Private Limited Companies',
      'Companies with regular operations',
      'Startups maintaining compliance',
      'Companies avoiding ROC penalties',
    ],
    
    whatsIncluded: [
      'Annual return (MGT-7/MGT-7A) filing',
      'Financial statements (AOC-4) filing',
      'Director KYC (DIR-3 KYC) for all directors',
      'Board meeting minutes preparation',
      'AGM minutes drafting',
      'Statutory registers maintenance',
      'Compliance calendar & reminders',
    ],
    
    howItWorks: [
      'Share audited financials & director details',
      'We prepare all MCA forms',
      'Review and approve before filing',
      'Receive filing acknowledgments',
    ],
    
    documentsRequired: [
      'Audited financial statements',
      'Auditor\'s report',
      'Director details & DIN',
      'Shareholding pattern',
      'Previous year filings',
      'Board resolution copies',
    ],
    
    taxationStructure: [
      'No direct tax impact from compliance filings',
      'Penalties for late filing: ₹100/day per form',
      'Additional fees for delayed DIR-3 KYC: ₹5,000',
      'Strike-off risk for non-compliance over 2 years',
    ],
    
    timeline: 'Filed within due dates',
    timelineNote: 'AOC-4: 30 days from AGM, MGT-7: 60 days from AGM',
    
    pricing: {
      professional: 5999,
      governmentFees: 'At actuals (based on authorized capital)',
      notes: ['Full year coverage', 'Govt fees extra'],
    },
    
    notIncluded: [
      'Statutory audit',
      'Income tax return filing',
      'Event-based filings (director change, etc.)',
    ],
  },

  'llp-compliance': {
    name: 'LLP Annual Compliance',
    category: 'MCA',
    categorySlug: 'mca',
    overview: 'Annual compliance package for LLPs including Form 8 and Form 11 filing. Simpler than company compliance but equally important for maintaining good standing.',
    
    whoShouldChoose: [
      'All registered LLPs',
      'LLPs with or without business activity',
      'LLPs maintaining active status',
      'Partners avoiding penalties',
    ],
    
    whatsIncluded: [
      'Form 8 (Statement of Accounts) filing',
      'Form 11 (Annual Return) filing',
      'Partner KYC updates',
      'Compliance calendar setup',
      'Designated partner coordination',
      'Filing acknowledgments',
    ],
    
    howItWorks: [
      'Share financial data & partner details',
      'We prepare Form 8 & Form 11',
      'Partners review & approve',
      'File before due dates',
    ],
    
    documentsRequired: [
      'Statement of accounts',
      'Partner contribution details',
      'Partner address & KYC documents',
      'Previous year filings',
      'Bank statements',
    ],
    
    taxationStructure: [
      'LLP taxed at 30% flat rate',
      'Late filing penalty: ₹100/day',
      'No audit required if turnover < ₹40 lakh',
      'Partners taxed separately on remuneration',
    ],
    
    timeline: 'Filed within due dates',
    timelineNote: 'Form 11: May 30, Form 8: Oct 30',
    
    pricing: {
      professional: 4999,
      governmentFees: 'At actuals (minimal filing fees)',
      notes: ['Both forms included', 'Govt fees extra'],
    },
    
    notIncluded: [
      'Income tax return filing',
      'LLP agreement amendments',
      'Partner changes',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // TRADEMARK & IP
  // ═══════════════════════════════════════════════════════════════════

  'trademark-registration': {
    name: 'Trademark Registration',
    category: 'Trademark',
    categorySlug: 'trademark',
    overview: 'Protect your brand name, logo, or tagline with trademark registration. Get exclusive rights to use your brand identity across India for 10 years.',
    
    whoShouldChoose: [
      'Businesses with unique brand names',
      'Startups protecting their identity',
      'E-commerce sellers & D2C brands',
      'Service providers with distinctive names',
    ],
    
    whatsIncluded: [
      'Trademark search & availability check',
      'Class identification & selection',
      'Application drafting & filing',
      'TM-A filing with trademark registry',
      'Application tracking',
      'Examination report handling',
      'Registration certificate (upon approval)',
    ],
    
    howItWorks: [
      'Share brand name/logo details',
      'We conduct trademark search',
      'File application with appropriate class',
      'Track until registration or respond to objections',
    ],
    
    documentsRequired: [
      'Brand name or logo (JPEG format)',
      'Applicant PAN & address proof',
      'Business registration documents',
      'User affidavit (if claiming prior use)',
      'Authorization letter',
    ],
    
    taxationStructure: [
      'No direct tax on trademark registration',
      'Trademark is an intangible asset',
      'Can be amortized over useful life',
      'GST applicable on professional services',
    ],
    
    timeline: '12–18 months for complete registration',
    timelineNote: 'TM symbol available immediately, ® after registration',
    
    pricing: {
      professional: 1499,
      governmentFees: '₹4,500 (individual/startup) or ₹9,000 (others)',
      notes: ['Per class pricing', 'Objection response charged separately'],
    },
    
    notIncluded: [
      'Trademark objection handling',
      'Opposition proceedings',
      'Trademark renewal (after 10 years)',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // ADDITIONAL BUSINESS SETUP SERVICES
  // ═══════════════════════════════════════════════════════════════════

  'section-8': {
    name: 'Section 8 Company',
    category: 'Business Setup',
    categorySlug: 'business-setup',
    overview: 'Section 8 Company is a non-profit organization registered under the Companies Act. Ideal for charitable, educational, or social welfare purposes with tax benefits.',
    
    whoShouldChoose: [
      'NGOs and charitable organizations',
      'Educational institutions',
      'Social welfare organizations',
      'Religious or cultural bodies',
    ],
    
    whatsIncluded: [
      'Name reservation with MCA',
      'MOA & AOA drafting for Section 8',
      'License application to Regional Director',
      'Incorporation certificate',
      'PAN & TAN for the company',
      'DIN for directors',
    ],
    
    howItWorks: [
      'Reserve company name with MCA',
      'Apply for Section 8 license',
      'Prepare incorporation documents',
      'Receive Certificate of Incorporation',
    ],
    
    documentsRequired: [
      'PAN & Aadhaar of all directors',
      'Address proof of directors',
      'Registered office address proof',
      'Passport-size photographs',
      'Declaration of charitable objective',
    ],
    
    taxationStructure: [
      'Exempt from income tax under Section 12A',
      'Donors get 80G tax benefit',
      'No dividend distribution allowed',
      'Profits must be used for charitable purposes',
    ],
    
    timeline: '15–25 working days',
    timelineNote: 'Subject to Regional Director approval',
    
    pricing: {
      professional: 14999,
      governmentFees: 'At actuals (varies by authorized capital)',
      notes: ['Includes license application', 'Govt fees extra'],
    },
    
    notIncluded: [
      '12A & 80G registration',
      'Annual compliance filings',
      'FCRA registration',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // LICENSES SERVICES
  // ═══════════════════════════════════════════════════════════════════

  'fssai-registration': {
    name: 'FSSAI Registration',
    category: 'Licenses',
    categorySlug: 'licenses',
    overview: 'FSSAI Registration is mandatory for all food businesses in India. Get your food license to legally manufacture, store, distribute, or sell food products.',
    
    whoShouldChoose: [
      'Food manufacturers & processors',
      'Restaurants & food outlets',
      'Food importers & exporters',
      'Online food delivery businesses',
    ],
    
    whatsIncluded: [
      'FSSAI application preparation',
      'Document verification',
      'Application filing on FoSCoS portal',
      'Application tracking',
      'License certificate delivery',
      'Compliance guidance',
    ],
    
    howItWorks: [
      'Share business & food category details',
      'Upload required documents',
      'We file FSSAI application',
      'Receive FSSAI license/registration',
    ],
    
    documentsRequired: [
      'PAN & Aadhaar of proprietor/directors',
      'Business address proof',
      'Food safety management plan',
      'List of food products',
      'Passport-size photograph',
    ],
    
    taxationStructure: [
      'No direct tax impact',
      'FSSAI fee based on business type',
      'Registration valid for 1-5 years',
      'Renewal required before expiry',
    ],
    
    timeline: '7–15 working days',
    timelineNote: 'Subject to food authority processing',
    
    pricing: {
      professional: 2499,
      governmentFees: '₹100–₹5,000 (based on license type)',
      notes: ['Includes document preparation', 'Govt fees vary by type'],
    },
    
    notIncluded: [
      'FSSAI annual return filing',
      'FSSAI license renewal',
      'Food testing & certification',
    ],
  },

  'fssai-renewal': {
    name: 'FSSAI Renewal',
    category: 'Licenses',
    categorySlug: 'licenses',
    overview: 'Renew your FSSAI license before expiry to continue food business operations without interruption. Timely renewal avoids penalties and business disruption.',
    
    whoShouldChoose: [
      'Existing FSSAI license holders',
      'Food businesses with expiring licenses',
      'Those wanting hassle-free renewal',
    ],
    
    whatsIncluded: [
      'License status verification',
      'Renewal application preparation',
      'Document update if required',
      'Application filing on FoSCoS',
      'New license certificate delivery',
    ],
    
    howItWorks: [
      'Share existing FSSAI license details',
      'We verify and prepare renewal',
      'File renewal application',
      'Receive renewed license',
    ],
    
    documentsRequired: [
      'Existing FSSAI license copy',
      'Updated business documents',
      'Food safety compliance report',
      'ID proof of authorized signatory',
    ],
    
    taxationStructure: [
      'Renewal fees same as fresh registration',
      'Late fee applicable if expired',
      'No tax deduction available',
    ],
    
    timeline: '5–10 working days',
    timelineNote: 'Apply 30 days before expiry for smooth renewal',
    
    pricing: {
      professional: 2499,
      governmentFees: '₹100–₹5,000 (based on license type)',
      notes: ['Same as fresh registration', 'Late fees extra if expired'],
    },
    
    notIncluded: [
      'License modification',
      'Adding new food categories',
      'Address change filing',
    ],
  },

  'iec': {
    name: 'Import Export Code (IEC)',
    category: 'Licenses',
    categorySlug: 'licenses',
    overview: 'IEC is mandatory for importing or exporting goods from India. Get your 10-digit IEC code to start international trade legally.',
    
    whoShouldChoose: [
      'Importers & exporters',
      'E-commerce businesses selling globally',
      'Manufacturers exporting products',
      'Trading companies',
    ],
    
    whatsIncluded: [
      'IEC application preparation',
      'Document verification',
      'DGFT portal registration',
      'Application filing & tracking',
      'IEC certificate delivery',
      'Post-registration guidance',
    ],
    
    howItWorks: [
      'Share business & bank details',
      'Upload required documents',
      'We file IEC application',
      'Receive IEC certificate',
    ],
    
    documentsRequired: [
      'PAN of business/proprietor',
      'Aadhaar of authorized signatory',
      'Business address proof',
      'Cancelled cheque/bank certificate',
      'Passport-size photograph',
    ],
    
    taxationStructure: [
      'No tax on IEC registration',
      'Export income may have tax benefits',
      'GST applicable on imports',
      'IEC valid for lifetime (no renewal)',
    ],
    
    timeline: '3–5 working days',
    timelineNote: 'Subject to DGFT processing',
    
    pricing: {
      professional: 2999,
      governmentFees: '₹500 (DGFT fee)',
      notes: ['Lifetime validity', 'No renewal required'],
    },
    
    notIncluded: [
      'RCMC registration',
      'Export documentation',
      'Customs clearance support',
    ],
  },

  'msme': {
    name: 'MSME / Udyam Registration',
    category: 'Licenses',
    categorySlug: 'licenses',
    overview: 'Udyam Registration (formerly MSME) provides government benefits including priority lending, subsidies, and tax benefits for small businesses.',
    
    whoShouldChoose: [
      'Small & medium enterprises',
      'Startups seeking government benefits',
      'Businesses applying for tenders',
      'Those seeking priority sector lending',
    ],
    
    whatsIncluded: [
      'Udyam portal registration',
      'Business classification assessment',
      'Application filing',
      'Udyam certificate delivery',
      'Benefits guidance',
    ],
    
    howItWorks: [
      'Share business & Aadhaar details',
      'We classify your enterprise',
      'File Udyam registration',
      'Receive Udyam certificate',
    ],
    
    documentsRequired: [
      'Aadhaar of proprietor/partner/director',
      'PAN of business',
      'Business address proof',
      'Bank account details',
    ],
    
    taxationStructure: [
      'No registration fee',
      'Tax benefits for MSMEs',
      'Priority sector lending eligibility',
      'Government tender preferences',
    ],
    
    timeline: '1–3 working days',
    timelineNote: 'Instant in most cases',
    
    pricing: {
      professional: 999,
      governmentFees: 'Nil (free registration)',
      notes: ['Government scheme - no fees', 'Lifetime validity'],
    },
    
    notIncluded: [
      'MSME loan assistance',
      'Tender registration',
      'Subsidy application support',
    ],
  },

  'bis': {
    name: 'BIS Certification',
    category: 'Licenses',
    categorySlug: 'licenses',
    overview: 'BIS Certification ensures your products meet Indian safety and quality standards. Mandatory for electronics, appliances, and other regulated products.',
    
    whoShouldChoose: [
      'Electronics manufacturers',
      'Home appliance makers',
      'Product importers',
      'E-commerce sellers of regulated goods',
    ],
    
    whatsIncluded: [
      'Product category assessment',
      'BIS application preparation',
      'Documentation support',
      'Liaison with BIS office',
      'Certification tracking',
      'License delivery',
    ],
    
    howItWorks: [
      'Share product details & specifications',
      'We assess certification requirements',
      'Prepare and file BIS application',
      'Coordinate testing & inspection',
    ],
    
    documentsRequired: [
      'Product specifications',
      'Test reports from accredited lab',
      'Manufacturing process details',
      'Quality control documents',
      'Factory address proof',
    ],
    
    taxationStructure: [
      'BIS fees vary by product category',
      'Testing charges extra',
      'Annual surveillance fees applicable',
      'GST on professional services',
    ],
    
    timeline: '45–90 working days',
    timelineNote: 'Depends on product category and testing',
    
    pricing: {
      professional: 14999,
      governmentFees: 'At actuals (varies by product)',
      notes: ['Product testing extra', 'Timeline varies by category'],
    },
    
    notIncluded: [
      'Product testing fees',
      'Factory inspection charges',
      'Annual surveillance',
    ],
  },

  'iso': {
    name: 'ISO Certification',
    category: 'Licenses',
    categorySlug: 'licenses',
    overview: 'ISO Certification demonstrates your commitment to quality, safety, and efficiency. Enhances credibility with clients and opens doors to new business opportunities.',
    
    whoShouldChoose: [
      'Businesses seeking quality certification',
      'Companies bidding for contracts',
      'Exporters needing international standards',
      'Organizations improving processes',
    ],
    
    whatsIncluded: [
      'ISO standard selection guidance',
      'Gap analysis & documentation',
      'Implementation support',
      'Internal audit preparation',
      'Certification body liaison',
      'Certificate delivery',
    ],
    
    howItWorks: [
      'Assess current processes',
      'Implement ISO requirements',
      'Conduct internal audit',
      'Certification audit & certificate',
    ],
    
    documentsRequired: [
      'Business registration documents',
      'Organizational structure',
      'Process documentation',
      'Quality policy & objectives',
      'Previous certifications (if any)',
    ],
    
    taxationStructure: [
      'ISO certification is a business expense',
      'Can be claimed as deduction',
      'Annual surveillance audit fees',
      'Recertification every 3 years',
    ],
    
    timeline: '30–60 working days',
    timelineNote: 'Depends on organization size and readiness',
    
    pricing: {
      professional: 9999,
      governmentFees: 'Certification body fees extra',
      notes: ['ISO 9001/14001/27001 etc.', 'Audit fees separate'],
    },
    
    notIncluded: [
      'Certification body audit fees',
      'Annual surveillance audits',
      'Recertification (after 3 years)',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // ADDITIONAL TRADEMARK SERVICES
  // ═══════════════════════════════════════════════════════════════════

  'trademark-objection': {
    name: 'Trademark Objection Reply',
    category: 'Trademark',
    categorySlug: 'trademark',
    overview: 'Respond to trademark examination objections professionally to save your trademark application from rejection.',
    
    whoShouldChoose: [
      'Applicants with examination report',
      'Those facing trademark objections',
      'Applications requiring clarification',
    ],
    
    whatsIncluded: [
      'Objection analysis',
      'Reply drafting by IP expert',
      'Supporting document preparation',
      'Reply filing with registry',
      'Follow-up tracking',
    ],
    
    howItWorks: [
      'Share examination report',
      'We analyze objection grounds',
      'Draft comprehensive reply',
      'File and track response',
    ],
    
    documentsRequired: [
      'Trademark application copy',
      'Examination report',
      'Evidence of use (if applicable)',
      'Authorization letter',
    ],
    
    taxationStructure: [
      'Professional service fee',
      'No government fee for reply',
      'GST applicable',
    ],
    
    timeline: '7–14 working days',
    timelineNote: 'Reply must be filed within 30 days of objection',
    
    pricing: {
      professional: 2999,
      governmentFees: 'Nil',
      notes: ['Per objection reply', 'Hearing charges extra'],
    },
    
    notIncluded: [
      'Trademark hearing representation',
      'Appeal filing',
      'Additional class objections',
    ],
  },

  'trademark-renewal': {
    name: 'Trademark Renewal',
    category: 'Trademark',
    categorySlug: 'trademark',
    overview: 'Renew your registered trademark before expiry to maintain exclusive rights for another 10 years.',
    
    whoShouldChoose: [
      'Trademark owners with expiring marks',
      'Those wanting continued protection',
      'Businesses maintaining brand rights',
    ],
    
    whatsIncluded: [
      'Trademark status verification',
      'Renewal application preparation',
      'Filing with trademark registry',
      'Renewal certificate tracking',
    ],
    
    howItWorks: [
      'Share trademark registration details',
      'We verify and prepare renewal',
      'File renewal application',
      'Receive renewal certificate',
    ],
    
    documentsRequired: [
      'Trademark registration certificate',
      'ID proof of owner',
      'Authorization letter',
    ],
    
    taxationStructure: [
      'Government fee: ₹9,000 (before expiry)',
      'Late fee: ₹4,500 (within grace period)',
      'Restoration fee if lapsed',
    ],
    
    timeline: '3–6 months',
    timelineNote: 'Apply 6 months before expiry',
    
    pricing: {
      professional: 2499,
      governmentFees: '₹9,000 (or ₹13,500 with late fee)',
      notes: ['Per class renewal', 'Apply before expiry'],
    },
    
    notIncluded: [
      'Trademark restoration',
      'Assignment/transfer',
      'Additional class renewal',
    ],
  },

  'copyright-registration': {
    name: 'Copyright Registration',
    category: 'Trademark',
    categorySlug: 'trademark',
    overview: 'Protect your original creative works including literary, artistic, musical, and software creations with copyright registration.',
    
    whoShouldChoose: [
      'Authors & writers',
      'Artists & designers',
      'Software developers',
      'Musicians & composers',
    ],
    
    whatsIncluded: [
      'Copyright application preparation',
      'Work categorization',
      'Documentation support',
      'Filing with Copyright Office',
      'Registration certificate tracking',
    ],
    
    howItWorks: [
      'Share details of creative work',
      'We categorize and document',
      'File copyright application',
      'Receive registration certificate',
    ],
    
    documentsRequired: [
      'Copy of the work',
      'Author/creator details',
      'NOC from publisher (if applicable)',
      'ID proof of applicant',
    ],
    
    taxationStructure: [
      'Copyright is an intangible asset',
      'Royalty income taxable',
      'Can be licensed or sold',
      'Lifetime + 60 years protection',
    ],
    
    timeline: '2–4 months',
    timelineNote: 'Subject to Copyright Office processing',
    
    pricing: {
      professional: 4999,
      governmentFees: '₹500–₹2,000 (based on work type)',
      notes: ['Per work registration', 'Govt fees vary'],
    },
    
    notIncluded: [
      'Copyright objection handling',
      'Infringement notices',
      'Licensing agreements',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // ADDITIONAL GST SERVICES
  // ═══════════════════════════════════════════════════════════════════

  'gst-modification': {
    name: 'GST Modification',
    category: 'GST',
    categorySlug: 'gst',
    overview: 'Update your GST registration details including business name, address, partners/directors, or other core information.',
    
    whoShouldChoose: [
      'Businesses with changed details',
      'Those relocating office address',
      'Companies with director changes',
      'Businesses changing constitution',
    ],
    
    whatsIncluded: [
      'Amendment application preparation',
      'Document verification',
      'Filing on GST portal',
      'Application tracking',
      'Updated certificate delivery',
    ],
    
    howItWorks: [
      'Share details to be modified',
      'We prepare amendment application',
      'File GST amendment',
      'Receive updated registration',
    ],
    
    documentsRequired: [
      'Existing GST certificate',
      'Proof of changed details',
      'Authorization letter',
      'Supporting documents',
    ],
    
    taxationStructure: [
      'No government fee for amendment',
      'Some changes require approval',
      'Timeline varies by change type',
    ],
    
    timeline: '7–15 working days',
    timelineNote: 'Core field changes may take longer',
    
    pricing: {
      professional: 999,
      governmentFees: 'Nil',
      notes: ['Per amendment', 'Complex changes may vary'],
    },
    
    notIncluded: [
      'Multiple amendments',
      'GST cancellation',
      'New registration',
    ],
  },

  'gst-cancellation': {
    name: 'GST Cancellation',
    category: 'GST',
    categorySlug: 'gst',
    overview: 'Cancel your GST registration if you have closed business, dropped below threshold, or no longer require GSTIN.',
    
    whoShouldChoose: [
      'Businesses closing operations',
      'Turnover dropped below threshold',
      'Duplicate registrations',
      'Those converting business type',
    ],
    
    whatsIncluded: [
      'Cancellation application preparation',
      'Final return filing (GSTR-10)',
      'Application submission',
      'Cancellation tracking',
      'Confirmation certificate',
    ],
    
    howItWorks: [
      'Share cancellation reason',
      'We file pending returns',
      'Submit cancellation application',
      'Receive cancellation order',
    ],
    
    documentsRequired: [
      'GST registration certificate',
      'Stock details (if any)',
      'Reason for cancellation',
      'Authorization letter',
    ],
    
    taxationStructure: [
      'Pending tax must be cleared',
      'ITC reversal may apply',
      'Final return mandatory',
      'No government fee',
    ],
    
    timeline: '15–30 working days',
    timelineNote: 'All pending returns must be filed',
    
    pricing: {
      professional: 999,
      governmentFees: 'Nil',
      notes: ['GSTR-10 filing included', 'Pending returns extra'],
    },
    
    notIncluded: [
      'Pending return filing',
      'Tax payment',
      'Revocation of cancellation',
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // ADDITIONAL TAX & PAYROLL SERVICES
  // ═══════════════════════════════════════════════════════════════════

  'tds-return': {
    name: 'TDS Return Filing',
    category: 'Tax & Payroll',
    categorySlug: 'tax-payroll',
    overview: 'File quarterly TDS returns accurately and on time to avoid penalties. We handle all forms including 24Q, 26Q, 27Q, and 27EQ.',
    
    whoShouldChoose: [
      'Employers deducting TDS on salary',
      'Businesses making TDS payments',
      'Those with contractor/vendor TDS',
      'Companies with rent/professional TDS',
    ],
    
    whatsIncluded: [
      'TDS data compilation',
      'Form preparation (24Q/26Q/27Q/27EQ)',
      'Challan verification',
      'Return filing on TRACES',
      'Filing acknowledgment',
    ],
    
    howItWorks: [
      'Share TDS payment details',
      'We compile and verify data',
      'Prepare and file TDS return',
      'Receive acknowledgment',
    ],
    
    documentsRequired: [
      'TDS payment challans',
      'Deductee details (PAN, amount)',
      'Previous quarter returns',
      'TAN details',
    ],
    
    taxationStructure: [
      'Late filing fee: ₹200/day',
      'Interest on late TDS: 1-1.5%/month',
      'Penalty for incorrect return',
      'Quarterly due dates apply',
    ],
    
    timeline: '3–5 working days',
    timelineNote: 'Subject to data availability',
    
    pricing: {
      professional: 1499,
      governmentFees: 'Nil',
      notes: ['Per quarter', 'Per return type'],
    },
    
    notIncluded: [
      'TDS payment',
      'Revised return filing',
      'Lower deduction certificate',
    ],
  },

  'it-notice': {
    name: 'Income Tax Notice Reply',
    category: 'Tax & Payroll',
    categorySlug: 'tax-payroll',
    overview: 'Professional response to income tax notices including scrutiny, demand, and clarification notices to protect your interests.',
    
    whoShouldChoose: [
      'Individuals receiving IT notices',
      'Businesses under scrutiny',
      'Those with demand notices',
      'Taxpayers needing clarification',
    ],
    
    whatsIncluded: [
      'Notice analysis',
      'Response drafting',
      'Supporting document preparation',
      'Reply filing on e-filing portal',
      'Follow-up tracking',
    ],
    
    howItWorks: [
      'Share notice details',
      'We analyze and strategize',
      'Prepare comprehensive reply',
      'File response and track',
    ],
    
    documentsRequired: [
      'Income tax notice copy',
      'Relevant ITRs',
      'Supporting documents',
      'Bank statements if required',
    ],
    
    taxationStructure: [
      'Reply deadline varies by notice type',
      'Penalty may apply for non-compliance',
      'Interest on demand if applicable',
    ],
    
    timeline: '5–10 working days',
    timelineNote: 'Before notice deadline',
    
    pricing: {
      professional: 2999,
      governmentFees: 'Nil',
      notes: ['Per notice', 'Complex cases may vary'],
    },
    
    notIncluded: [
      'Personal hearing representation',
      'Appeal filing',
      'Tax payment',
    ],
  },

  'pf-registration': {
    name: 'PF (EPF) Registration',
    category: 'Tax & Payroll',
    categorySlug: 'tax-payroll',
    overview: 'Register your establishment with EPFO to provide provident fund benefits to employees. Mandatory for businesses with 20+ employees.',
    
    whoShouldChoose: [
      'Businesses with 20+ employees',
      'Voluntary registration seekers',
      'Startups planning to hire',
      'Companies expanding workforce',
    ],
    
    whatsIncluded: [
      'EPFO registration application',
      'Document preparation',
      'Portal registration',
      'PF code allotment',
      'Compliance guidance',
    ],
    
    howItWorks: [
      'Share business & employee details',
      'We prepare registration application',
      'File with EPFO portal',
      'Receive PF establishment code',
    ],
    
    documentsRequired: [
      'Business registration documents',
      'PAN of establishment',
      'Employee details',
      'Bank account details',
      'Rent agreement/ownership proof',
    ],
    
    taxationStructure: [
      'Employer contribution: 12% of basic',
      'Employee contribution: 12% of basic',
      'Admin charges: 0.5%',
      'EDLI: 0.5%',
    ],
    
    timeline: '7–14 working days',
    timelineNote: 'Subject to EPFO verification',
    
    pricing: {
      professional: 2999,
      governmentFees: 'Nil',
      notes: ['One-time registration', 'Monthly filing separate'],
    },
    
    notIncluded: [
      'Monthly PF return filing',
      'Employee PF transfer',
      'PF withdrawal assistance',
    ],
  },

  'esi-registration': {
    name: 'ESIC Registration',
    category: 'Tax & Payroll',
    categorySlug: 'tax-payroll',
    overview: 'Register with ESIC to provide medical and cash benefits to employees. Mandatory for establishments with 10+ employees in notified areas.',
    
    whoShouldChoose: [
      'Businesses with 10+ employees',
      'Factories and establishments',
      'Companies in notified areas',
      'Those paying wages up to ₹21,000',
    ],
    
    whatsIncluded: [
      'ESIC registration application',
      'Document preparation',
      'Portal registration',
      'ESIC code allotment',
      'Compliance guidance',
    ],
    
    howItWorks: [
      'Share establishment details',
      'We prepare ESI application',
      'File with ESIC portal',
      'Receive ESIC code',
    ],
    
    documentsRequired: [
      'Business registration documents',
      'PAN of establishment',
      'Employee details with wages',
      'Bank account details',
      'Address proof',
    ],
    
    taxationStructure: [
      'Employer contribution: 3.25%',
      'Employee contribution: 0.75%',
      'Applied on wages up to ₹21,000',
      'Monthly filing required',
    ],
    
    timeline: '7–14 working days',
    timelineNote: 'Subject to ESIC verification',
    
    pricing: {
      professional: 2999,
      governmentFees: 'Nil',
      notes: ['One-time registration', 'Monthly filing separate'],
    },
    
    notIncluded: [
      'Monthly ESI return filing',
      'Employee registration',
      'Claim processing support',
    ],
  },

  'pf-esi-return': {
    name: 'PF / ESIC Return Filing',
    category: 'Tax & Payroll',
    categorySlug: 'tax-payroll',
    overview: 'Monthly filing of PF and ESIC returns to ensure compliance and avoid penalties. Includes challan generation and payment support.',
    
    whoShouldChoose: [
      'PF registered establishments',
      'ESIC registered businesses',
      'Those outsourcing compliance',
      'HR teams needing support',
    ],
    
    whatsIncluded: [
      'Monthly data compilation',
      'ECR/ESIC return preparation',
      'Challan generation',
      'Return filing on portal',
      'Filing confirmation',
    ],
    
    howItWorks: [
      'Share monthly employee data',
      'We prepare returns',
      'Generate challans',
      'File returns after payment',
    ],
    
    documentsRequired: [
      'Employee attendance & wages',
      'New joiner details',
      'Exit employee details',
      'Payment challans',
    ],
    
    taxationStructure: [
      'Due date: 15th of following month',
      'Late filing penalty applies',
      'Interest on delayed payment',
    ],
    
    timeline: '2–3 working days',
    timelineNote: 'After receiving complete data',
    
    pricing: {
      professional: 999,
      governmentFees: 'Nil',
      notes: ['Per month per return', 'Both PF & ESI included'],
    },
    
    notIncluded: [
      'Payment of PF/ESI dues',
      'Employee grievance handling',
      'Annual returns',
    ],
  },
};

// Category mapping for URL routing
export const categoryMapping = {
  'business-setup': ['proprietorship', 'partnership', 'opc', 'llp', 'private-limited', 'section-8'],
  'licenses': ['fssai-registration', 'fssai-renewal', 'iec', 'msme', 'bis', 'iso'],
  'trademark': ['trademark-registration', 'trademark-objection', 'trademark-renewal', 'copyright-registration'],
  'gst': ['gst-registration', 'gst-return', 'gst-modification', 'gst-cancellation'],
  'tax-payroll': ['itr-efiling', 'business-tax', 'tds-return', 'it-notice', 'pf-registration', 'esi-registration', 'pf-esi-return'],
  'mca': ['company-compliance', 'llp-compliance'],
  // Legacy mappings for backward compatibility
  'startup': ['proprietorship', 'partnership', 'opc', 'llp', 'private-limited'],
  'income-tax': ['itr-efiling', 'business-tax'],
};

// Get service by slug
export const getServiceBySlug = (slug) => {
  return serviceContent[slug] || null;
};

// Get all services in a category
export const getServicesByCategory = (categorySlug) => {
  const slugs = categoryMapping[categorySlug] || [];
  return slugs.map(slug => ({ slug, ...serviceContent[slug] })).filter(Boolean);
};

export default serviceContent;
